import React, { useState, useEffect } from 'react';
import { usePaneNavigate } from '../lib/paneNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowRight, FileText, Trash2, AlertTriangle, CheckCircle, MinusCircle, GraduationCap, Search, UserCheck } from 'lucide-react';
import { getConsolidatedInvoices, deleteConsolidatedInvoice, createConsolidatedInvoice, getCustomers, getDailyLogs, getServiceCalls, getCustomerBalance } from '../api';
import { toast } from '../hooks/use-toast';
import { matchesCustomer } from '../lib/customerSearch';

const ConsolidatedInvoicesList = () => {
  const navigate = usePaneNavigate();
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Customer selection
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerQuery, setCustomerQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Open logs for selected customer
  const [openLogs, setOpenLogs] = useState([]);
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [customerBalance, setCustomerBalance] = useState(null);

  // College dialog
  const [showCollegeDialog, setShowCollegeDialog] = useState(false);
  const [collegeCalls, setCollegeCalls] = useState([]);
  const [selectedCalls, setSelectedCalls] = useState(new Set());
  const [isLoadingCalls, setIsLoadingCalls] = useState(false);

  useEffect(() => {
    loadInvoices();
    loadCustomers();
  }, []);

  const loadInvoices = async () => {
    try {
      const data = await getConsolidatedInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setAllCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  // Customer autocomplete
  const handleCustomerSearch = (val) => {
    setCustomerQuery(val);
    if (val.trim().length > 0) {
      const matches = allCustomers.filter(c => matchesCustomer(c, val));
      setSuggestions(matches);
      setShowSuggestions(matches.length > 0);
    } else {
      setShowSuggestions(false);
      setSelectedCustomer(null);
      setOpenLogs([]);
    }
  };

  const selectCustomer = async (customer) => {
    const fullName = `${customer.firstName} ${customer.lastName}`.trim();
    setCustomerQuery(fullName);
    setSelectedCustomer({ ...customer, fullName });
    setShowSuggestions(false);
    setSelectedLogs(new Set());

    // Load open logs + balance in parallel
    setIsLoadingLogs(true);
    try {
      const [logs, balance] = await Promise.all([
        getDailyLogs(),
        getCustomerBalance(fullName)
      ]);
      const customerLogs = logs.filter(l =>
        !l.accountClosed && l.customerName === fullName
      );
      setOpenLogs(customerLogs);
      setCustomerBalance(balance);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleCreateConsolidated = async () => {
    if (selectedLogs.size === 0) {
      toast({ title: 'שגיאה', description: 'נא לבחור לפחות רישום אחד', variant: 'destructive' });
      return;
    }
    try {
      const invoice = await createConsolidatedInvoice({ logIds: Array.from(selectedLogs) });
      toast({ title: 'נוצר!', description: `חשבון מרוכז ל-${invoice.customerName} נוצר` });
      setSelectedCustomer(null);
      setCustomerQuery('');
      setOpenLogs([]);
      setSelectedLogs(new Set());
      navigate(`/consolidated-invoice?id=${invoice.id}`);
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן ליצור', variant: 'destructive' });
    }
  };

  // College functions
  const [collegeSearchQuery, setCollegeSearchQuery] = useState('');
  const [collegeDateFrom, setCollegeDateFrom] = useState('');
  const [collegeDateTo, setCollegeDateTo] = useState('');

  const openCollegeDialog = async () => {
    setShowCollegeDialog(true);
    setIsLoadingCalls(true);
    setSelectedCalls(new Set());
    setCollegeSearchQuery('');
    setCollegeDateFrom('');
    setCollegeDateTo('');
    try {
      const calls = await getServiceCalls();
      setCollegeCalls(calls);
    } catch (error) {
      toast({ title: 'שגיאה', description: 'לא ניתן לטעון קריאות', variant: 'destructive' });
    } finally {
      setIsLoadingCalls(false);
    }
  };

  const filteredCollegeCalls = collegeCalls.filter(c => {
    if (collegeSearchQuery.trim()) {
      const q = collegeSearchQuery.trim().toLowerCase();
      const name = (c.customerName || '').toLowerCase();
      const branch = (c.branch || '').toLowerCase();
      const desc = (c.issueDescription || '').toLowerCase();
      const contact = (c.contactName || '').toLowerCase();
      if (!(name.includes(q) || branch.includes(q) || desc.includes(q) || contact.includes(q))) {
        return false;
      }
    }
    if (collegeDateFrom || collegeDateTo) {
      if (!c.callDate) return false;
      const callDateStr = new Date(c.callDate).toISOString().slice(0, 10);
      if (collegeDateFrom && callDateStr < collegeDateFrom) return false;
      if (collegeDateTo && callDateStr > collegeDateTo) return false;
    }
    return true;
  });

  const handleCreateCollegeConsolidated = async () => {
    if (selectedCalls.size === 0) {
      toast({ title: 'שגיאה', description: 'נא לבחור לפחות קריאה אחת', variant: 'destructive' });
      return;
    }
    try {
      const invoice = await createConsolidatedInvoice({
        callIds: Array.from(selectedCalls),
        sourceType: 'service_calls',
        customerName: 'מכללה טכנולוגית'
      });
      toast({ title: 'נוצר!', description: 'חשבון מרוכז למכללה נוצר' });
      setShowCollegeDialog(false);
      navigate(`/consolidated-invoice?id=${invoice.id}`);
    } catch (error) {
      toast({ title: 'שגיאה', description: error.response?.data?.detail || 'לא ניתן ליצור', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('למחוק חשבון מרוכז זה?')) {
      try {
        await deleteConsolidatedInvoice(id);
        setInvoices(invoices.filter(i => i.id !== id));
        toast({ title: 'נמחק', description: 'החשבון הועבר לסל המחזור' });
      } catch (error) {
        toast({ title: 'שגיאה', description: 'לא ניתן למחוק', variant: 'destructive' });
      }
    }
  };

  const formatCurrency = (val) => `₪${parseFloat(val || 0).toFixed(2)}`;
  const calcLogTotal = (log) => (log.workItems || []).reduce((sum, item) => {
    return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.transactionAmount) || 0);
  }, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-emerald-50 py-8 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <FileText className="w-8 h-8 text-teal-600" />
              חשבונות מרוכזים
            </h1>
          </div>
          <Button onClick={() => navigate('/reports')} variant="outline" className="border-2">
            <ArrowRight className="ml-2 w-5 h-5" />
            חזור לדוחות
          </Button>
        </div>

        {/* Customer Search + College Button */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {/* Customer Autocomplete */}
          <div className="md:col-span-2 relative">
            <div className="bg-white rounded-xl border-2 border-teal-200 p-4 shadow-lg">
              <label className="text-sm font-bold text-gray-700 mb-2 block flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-teal-600" />
                בחר לקוח לחשבון מרוכז
              </label>
              <Input
                value={customerQuery}
                onChange={(e) => handleCustomerSearch(e.target.value)}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="הקלד שם או מספר לקוח..."
                className="text-lg py-5 border-2 focus:border-teal-500"
                autoComplete="off"
                data-testid="customer-search-input"
              />
              {showSuggestions && (
                <div className="absolute z-50 left-4 right-4 mt-1 bg-white border-2 border-teal-300 rounded-lg shadow-xl max-h-48 overflow-y-auto" data-testid="customer-suggestions">
                  {suggestions.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-right px-4 py-3 hover:bg-teal-50 transition-colors flex items-center justify-between border-b last:border-0"
                      onMouseDown={(e) => { e.preventDefault(); selectCustomer(c); }}
                    >
                      <span className="font-medium"><span className="font-mono font-bold text-gray-500 ml-2">#{c.customerNumber}</span>{c.firstName} {c.lastName}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.customerType === 'business' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {c.customerType === 'business' ? 'עסקי' : 'פרטי'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* College Button */}
          <div>
            <div className="bg-white rounded-xl border-2 border-purple-200 p-4 shadow-lg h-full flex flex-col justify-center">
              <Button
                onClick={openCollegeDialog}
                className="w-full py-5 text-base font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white"
                data-testid="college-consolidated-btn"
              >
                <GraduationCap className="ml-2 w-5 h-5" />
                מכללה טכנולוגית
              </Button>
              <p className="text-xs text-gray-500 mt-2 text-center">חשבון מרוכז מקריאות שירות</p>
            </div>
          </div>
        </div>

        {/* Open Logs for Selected Customer */}
        {selectedCustomer && (
          <>
            {/* Previous Balance Alert */}
            {customerBalance && Math.abs(customerBalance.totalBalance) > 0.01 && (
              <div className={`mb-4 p-4 rounded-xl border-2 flex items-center gap-3 ${
                customerBalance.totalBalance > 0 ? 'bg-red-50 border-red-300' : 'bg-blue-50 border-blue-300'
              }`} data-testid="previous-balance-alert">
                {customerBalance.totalBalance > 0 ? (
                  <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                ) : (
                  <MinusCircle className="w-6 h-6 text-blue-600 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`font-bold text-lg ${customerBalance.totalBalance > 0 ? 'text-red-800' : 'text-blue-800'}`}>
                    {customerBalance.totalBalance > 0
                      ? `יתרת חוב קודמת: ${formatCurrency(customerBalance.totalBalance)}`
                      : `עודף מחשבון קודם: ${formatCurrency(Math.abs(customerBalance.totalBalance))}`}
                  </p>
                  <p className="text-sm text-gray-600">
                    מ-{customerBalance.invoiceCount} חשבונות מרוכזים קודמים.
                    {customerBalance.totalBalance > 0
                      ? ' החוב יתווסף לחשבון המרוכז הבא.'
                      : ' העודף יקוזז מהחשבון המרוכז הבא.'}
                  </p>
                </div>
                <span className={`text-2xl font-black ${customerBalance.totalBalance > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                  {formatCurrency(Math.abs(customerBalance.totalBalance))}
                </span>
              </div>
            )}

            <Card className="mb-6 shadow-xl border-0" data-testid="customer-open-logs">
            <CardHeader className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
              <CardTitle className="text-xl flex items-center justify-between">
                <span>חשבוניות פתוחות - {selectedCustomer.fullName}</span>
                {selectedLogs.size > 0 && (
                  <Button
                    onClick={handleCreateConsolidated}
                    className="bg-white text-teal-700 hover:bg-teal-50 font-bold"
                    data-testid="create-consolidated-btn"
                  >
                    <FileText className="ml-2 w-4 h-4" />
                    צור חשבון מרוכז ({selectedLogs.size})
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoadingLogs ? (
                <p className="text-center py-8 text-gray-500">טוען...</p>
              ) : openLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-lg">אין חשבוניות פתוחות ל-{selectedCustomer.fullName}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-3 w-12 text-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={openLogs.length > 0 && openLogs.every(l => selectedLogs.has(l.id))}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedLogs(new Set(openLogs.map(l => l.id)));
                              else setSelectedLogs(new Set());
                            }}
                          />
                        </th>
                        <th className="border p-3 text-right">תאריך</th>
                        <th className="border p-3 text-right">פירוט עבודה</th>
                        <th className="border p-3 text-center">פריטים</th>
                        <th className="border p-3 text-center">סכום</th>
                        <th className="border p-3 text-center">שולם</th>
                        <th className="border p-3 text-center">יתרה</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openLogs.map((log, idx) => {
                        const total = calcLogTotal(log);
                        const paid = (log.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
                        const balance = total - paid;
                        return (
                          <tr
                            key={log.id}
                            className={`cursor-pointer ${selectedLogs.has(log.id) ? 'bg-teal-50 ring-2 ring-teal-300 ring-inset' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-teal-50`}
                            onClick={() => {
                              const next = new Set(selectedLogs);
                              if (next.has(log.id)) next.delete(log.id);
                              else next.add(log.id);
                              setSelectedLogs(next);
                            }}
                          >
                            <td className="border p-3 text-center">
                              <input
                                type="checkbox"
                                className="w-4 h-4"
                                checked={selectedLogs.has(log.id)}
                                readOnly
                              />
                            </td>
                            <td className="border p-3 text-sm whitespace-nowrap">
                              {new Date(log.date).toLocaleDateString('he-IL')}
                            </td>
                            <td className="border p-3 text-sm">
                              {(log.workItems || []).map(i => i.partName).filter(Boolean).join(', ') || '-'}
                            </td>
                            <td className="border p-3 text-center">{(log.workItems || []).length}</td>
                            <td className="border p-3 text-center font-bold text-blue-700">{formatCurrency(total)}</td>
                            <td className="border p-3 text-center text-green-700">{formatCurrency(paid)}</td>
                            <td className={`border p-3 text-center font-bold ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {formatCurrency(balance)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-teal-100 font-bold">
                        <td colSpan="4" className="border p-3 text-left">
                          סה"כ {openLogs.length} חשבוניות פתוחות
                        </td>
                        <td className="border p-3 text-center text-blue-800">
                          {formatCurrency(openLogs.reduce((s, l) => s + calcLogTotal(l), 0))}
                        </td>
                        <td className="border p-3 text-center text-green-800">
                          {formatCurrency(openLogs.reduce((s, l) => s + (l.payments || []).reduce((ps, p) => ps + (parseFloat(p.amount) || 0), 0), 0))}
                        </td>
                        <td className="border p-3 text-center text-red-800">
                          {formatCurrency(openLogs.reduce((s, l) => {
                            const t = calcLogTotal(l);
                            const p = (l.payments || []).reduce((ps, pp) => ps + (parseFloat(pp.amount) || 0), 0);
                            return s + (t - p);
                          }, 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </>
        )}

        {/* Existing Consolidated Invoices */}
        {invoices.length > 0 && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-3 mt-8">חשבונות מרוכזים קיימים ({invoices.length})</h2>
            <div className="space-y-3">
              {invoices.map((inv) => {
                const balance = inv.balance ?? (inv.totalAmount - (inv.totalPaid || 0));
                return (
                  <Card
                    key={inv.id}
                    className="shadow-lg border-0 hover:shadow-xl transition-shadow cursor-pointer"
                    onClick={() => navigate(`/consolidated-invoice?id=${inv.id}`)}
                    data-testid={`consolidated-card-${inv.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            balance > 0.01 ? 'bg-red-100' : balance < -0.01 ? 'bg-blue-100' : 'bg-green-100'
                          }`}>
                            {balance > 0.01 ? (
                              <AlertTriangle className="w-6 h-6 text-red-600" />
                            ) : balance < -0.01 ? (
                              <MinusCircle className="w-6 h-6 text-blue-600" />
                            ) : (
                              <CheckCircle className="w-6 h-6 text-green-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{inv.customerName}</h3>
                            <div className="flex gap-3 text-sm text-gray-500 mt-0.5">
                              <span>{(inv.logIds?.length || 0) + (inv.callIds?.length || 0)} רישומים</span>
                              <span>|</span>
                              <span>{inv.workItems?.length || 0} פריטים</span>
                              <span>|</span>
                              <span>{new Date(inv.createdAt).toLocaleDateString('he-IL')}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-left">
                            <p className="text-sm text-gray-500">סה"כ: {formatCurrency(inv.totalAmount)}</p>
                            <p className="text-sm text-gray-500">שולם: {formatCurrency(inv.totalPaid || 0)}</p>
                          </div>
                          <Badge className={`text-sm px-3 py-1 ${
                            balance > 0.01 ? 'bg-red-600' : balance < -0.01 ? 'bg-blue-600' : 'bg-green-600'
                          }`}>
                            {balance > 0.01 ? `חוב ${formatCurrency(balance)}` : balance < -0.01 ? `עודף ${formatCurrency(Math.abs(balance))}` : 'מאוזן'}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleDelete(inv.id); }}
                            className="text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Empty state when no customer selected and no invoices */}
        {!selectedCustomer && invoices.length === 0 && !isLoading && (
          <Card className="shadow-xl border-0">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl text-gray-500">בחר לקוח למעלה כדי לראות חשבוניות פתוחות</p>
              <p className="text-gray-400 mt-2">או לחץ על "מכללה טכנולוגית" ליצירת חשבון מקריאות שירות</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* College Service Calls Dialog */}
      <Dialog open={showCollegeDialog} onOpenChange={setShowCollegeDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <GraduationCap className="w-7 h-7 text-purple-600" />
              מכללה טכנולוגית - בחר קריאות שירות
            </DialogTitle>
          </DialogHeader>

          {isLoadingCalls ? (
            <p className="text-center py-8 text-gray-500">טוען קריאות שירות...</p>
          ) : collegeCalls.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p className="text-lg">לא נמצאו קריאות שירות במערכת</p>
            </div>
          ) : (
            <>
              {/* Search + date filter within service calls */}
              <div className="flex gap-2 items-center mb-3 flex-wrap">
                <Input
                  value={collegeSearchQuery}
                  onChange={(e) => setCollegeSearchQuery(e.target.value)}
                  placeholder="חפש לפי שם לקוח..."
                  className="flex-1 min-w-[160px] border-2"
                  data-testid="college-search-input"
                />
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-600 whitespace-nowrap">מתאריך:</label>
                  <Input
                    type="date"
                    value={collegeDateFrom}
                    onChange={(e) => setCollegeDateFrom(e.target.value)}
                    className="border-2 w-36"
                    data-testid="college-date-from"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <label className="text-sm font-medium text-gray-600 whitespace-nowrap">עד תאריך:</label>
                  <Input
                    type="date"
                    value={collegeDateTo}
                    onChange={(e) => setCollegeDateTo(e.target.value)}
                    className="border-2 w-36"
                    data-testid="college-date-to"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setCollegeSearchQuery(''); setCollegeDateFrom(''); setCollegeDateTo(''); }}
                  className="whitespace-nowrap"
                >
                  {(collegeSearchQuery || collegeDateFrom || collegeDateTo) ? 'נקה' : 'הכל'}
                </Button>
                <span className="text-sm text-gray-500 whitespace-nowrap">
                  {filteredCollegeCalls.length} / {collegeCalls.length}
                </span>
              </div>

              {selectedCalls.size > 0 && (
                <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-3 flex items-center justify-between">
                  <span className="font-bold text-purple-800">נבחרו {selectedCalls.size} קריאות</span>
                  <Button
                    onClick={handleCreateCollegeConsolidated}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="create-college-consolidated-btn"
                  >
                    <FileText className="ml-2 w-4 h-4" />
                    צור חשבון מרוכז
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto border rounded-lg mt-3">
                <table className="w-full border-collapse bg-white">
                  <thead>
                    <tr className="bg-purple-100">
                      <th className="border p-3 w-12 text-center">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={filteredCollegeCalls.length > 0 && filteredCollegeCalls.every(c => selectedCalls.has(c.id))}
                          onChange={(e) => {
                            if (e.target.checked) setSelectedCalls(new Set(filteredCollegeCalls.map(c => c.id)));
                            else setSelectedCalls(new Set());
                          }}
                        />
                      </th>
                      <th className="border p-3 text-right">תאריך</th>
                      <th className="border p-3 text-right">איש קשר</th>
                      <th className="border p-3 text-right">סניף</th>
                      <th className="border p-3 text-right">תיאור</th>
                      <th className="border p-3 text-center">פריטים</th>
                      <th className="border p-3 text-center">סכום</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCollegeCalls.map((call, idx) => {
                      const totalAmount = (call.workItems || []).reduce((sum, item) => {
                        return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.transactionAmount) || 0);
                      }, 0);
                      return (
                        <tr
                          key={call.id}
                          className={`${selectedCalls.has(call.id) ? 'bg-purple-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50 cursor-pointer`}
                          onClick={() => {
                            const next = new Set(selectedCalls);
                            if (next.has(call.id)) next.delete(call.id);
                            else next.add(call.id);
                            setSelectedCalls(next);
                          }}
                        >
                          <td className="border p-3 text-center">
                            <input type="checkbox" className="w-4 h-4" checked={selectedCalls.has(call.id)} readOnly />
                          </td>
                          <td className="border p-3 text-sm">
                            {call.callDate ? new Date(call.callDate).toLocaleDateString('he-IL') : '-'}
                          </td>
                          <td className="border p-3 font-medium text-gray-900">{call.customerName || '-'}</td>
                          <td className="border p-3 font-medium">{call.branch || '-'}</td>
                          <td className="border p-3 text-sm text-gray-700 max-w-xs truncate">{call.issueDescription || '-'}</td>
                          <td className="border p-3 text-center">{(call.workItems || []).length}</td>
                          <td className="border p-3 text-center font-bold text-blue-700">₪{totalAmount.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredCollegeCalls.length === 0 && collegeCalls.length > 0 && (
                <div className="text-center py-4 text-gray-500">
                  <p>לא נמצאו קריאות עם המסנן הנוכחי</p>
                  <Button variant="link" onClick={() => { setCollegeSearchQuery(''); setCollegeDateFrom(''); setCollegeDateTo(''); }} className="text-purple-600">
                    הצג את כל {collegeCalls.length} הקריאות
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsolidatedInvoicesList;
